import { NgModule } from '@angular/core';
import {
  Event,
  NavigationEnd,
  Router,
  RouterModule,
  Routes,
  Scroll,
  TitleStrategy
} from '@angular/router';
import { ViewportScroller } from '@angular/common';
import { timer, zip } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import { CustomPreloadingStrategy } from './preloading-strategy';

import {
  adminRoleGuard,
  authGuard,
  councelorRoleGuard,
  sessionSettledGuard,
  siteBlockGuard
} from './core-module/guards';
import { branchListResolver, userResolver } from './core-module/resolvers';
import { BUILD_TARGET, TAKE_SURVEY_URLS } from './shared-module/constants';
import { BUILD_YOUR_TARGET_PATH } from './core-module/constants/routes';
import { PasscodeComponent } from './core-module/components/passcode/passcode.component';

import { CpdTitleStrategy } from './core-module/services/title/cpd-title.strategy';

import { frontDoorGuard } from './features/home/front-door.guard';
import { ERROR_CODES } from './shared-module/constants/error.const';
const routes: Routes = [
  {
    /*
     * The redesigned home page IS the landing page.
     *
     * This sent everybody to `career-planning`, which redirects on to
     * `build-your-target`: the page the 02-home redesign replaces. So the
     * redesigned home page at /home was never what anybody landed on, and the
     * old page's Via banner was the first thing a member read.
     */
    path: '',
    redirectTo: `career-planning/${BUILD_YOUR_TARGET_PATH}`,
    pathMatch: 'full'
  },
  {
    path: 'career-planning',
    title: 'Career planning',
    loadChildren: () =>
      import(`./features/career-planning/career-planning.module`).then(
        (m) => m.CareerPlanningModule
      ),
    canActivate: [siteBlockGuard, frontDoorGuard]
  },
  {
    /*
     * THE REDESIGNED SKILLS SURVEY.
     *
     * Its own path rather than replacing `career-planning/build-your-target/
     * skills` yet: the shipped flow is eight routed steps behind resolvers and
     * guards, and swapping it out is a decision to make deliberately rather
     * than as a side effect of putting the new one on screen. Both are
     * reachable, so they can be compared.
     */
    path: 'skills-survey',
    title: 'Skills survey',
    /*
     * The branch list comes through the resolver, the way the shipped survey
     * gets it. `getBranchList()` returns a COLD observable, so calling it from
     * ngOnInit without subscribing fired no query at all and the Branch select
     * was permanently empty.
     *
     * The profile comes through `userResolver`, the same one the shipped
     * survey's parent route uses. Without it the store held `null` when the
     * component mounted, so the first Continue built its payload from an empty
     * object and the save deleted the member's work history.
     */
    resolve: { branchList: branchListResolver, userProfile: userResolver },
    loadComponent: () =>
      import(
        `./features/skills-survey/containers/skills-survey/skills-survey.component`
      ).then((m) => m.SkillsSurveyComponent),
    /*
     * The maintenance check only.
     *
     * `authGuard` was added here to keep a signed-out visitor from stalling on
     * step one, where the component list needs a session. It cannot be used on
     * a learner page: it reads `AuthSelectors.selectIsAuthenticated`, which
     * `deriveRestoredAuthorization` sets only for Councelor, CouncelorAdmin and
     * SuperAdmin, so every signed-in MEMBER failed it and was redirected to
     * /account/login and on to the home page. Every other authGuard in this
     * file sits on an admin route beside a role guard, which is what it is for.
     *
     * The data-loss reason the guard was reached for is handled by
     * `userResolver` above. A signed-out visitor can still stall on step one
     * for want of a component list; that wants a member-aware guard, which the
     * app does not have yet.
     */
    canActivate: [siteBlockGuard, frontDoorGuard]
  },
  {
    path: 'occupation',
    title: 'Occupations',
    loadChildren: () =>
      import(`./features/occupation/occupation.module`).then(
        (m) => m.OccupationModule
      ),
    canActivate: [siteBlockGuard, frontDoorGuard]
  },
  {
    path: 'pathway',
    title: 'Career pathways',
    loadChildren: () =>
      import(`./features/pathway/pathway.module`).then((m) => m.PathwayModule),
    canActivate: [siteBlockGuard, frontDoorGuard]
  },
  // PWS 4.1.19, spec 07. The public portfolio page: UNAUTHENTICATED, and with
  // no siteBlockGuard either, so a link an employer already holds does not
  // break during maintenance. The visitor is not the member.
  {
    path: 'p',
    loadChildren: () =>
      import('./features/public-page/public-page.module').then(
        (m) => m.PublicPageModule
      )
  },
  // PWS 4.1.7, spec 05 section 8. The counselor's read of a shared goals
  // report: UNAUTHENTICATED, and with no siteBlockGuard either, so a link a
  // counselor already holds does not break during maintenance. The reader is
  // not the member.
  {
    path: 'shared-report',
    title: 'Shared goals report',
    children: [
      {
        path: ':token',
        loadComponent: () =>
          import(
            './features/goals-report/containers/shared-report/shared-report.component'
          ).then((m) => m.SharedReportComponent)
      }
    ]
  },
  // PWS 4.1.10, 4.1.13, 4.1.15, 4.1.16, 4.1.17, 4.1.19. One of the four main
  // nav destinations, settled 2026-08-24: reachability from every page is part
  // of the requirement, not a nicety.
  {
    path: 'transition',
    loadChildren: () =>
      import('./features/transition/transition.module').then(
        (m) => m.TransitionModule
      ),
    canActivate: [siteBlockGuard, frontDoorGuard]
  },
  {
    /*
     * /home is kept as a redirect, not a page. The redesign now serves the
     * original URL, and every link, bookmark and deep link to /home still
     * lands on it rather than on a second copy that could drift.
     */
    path: 'home',
    redirectTo: `career-planning/${BUILD_YOUR_TARGET_PATH}`,
    pathMatch: 'full'
  },
  {
    /*
     * The redesign shipped this content twice: `features/help` here, and
     * ResourcesContainerComponent at /about/resources. /about/resources is
     * the one that ships, and it is what NAV_LINKS points at. `features/help`
     * was the static copy: it never called HelpArticles so it had no content
     * from the table, its tab strip had no keydown handler so the last three
     * tabs could not be reached from the keyboard, and its only counselor
     * call to action was an href="#". Nothing in the product linked here,
     * and a content comparison found every guide already present in
     * /about/resources, so the duplicate is deleted rather than kept behind
     * a URL.
     *
     * Redirected rather than removed, for the same reason /about/supporters
     * still redirects: the URL was live, so a bookmark or an emailed link
     * lands on the surviving page instead of a 404.
     */
    path: 'help',
    redirectTo: 'about/resources',
    pathMatch: 'full'
  },
  // Release scan, 1 Oct. The usual guesses for the site map land on it.
  { path: 'sitemap', redirectTo: 'about/site-map', pathMatch: 'full' },
  { path: 'site-map', redirectTo: 'about/site-map', pathMatch: 'full' },
  {
    /*
     * JOBS.1. Only the consent interstitial. The board itself is another site
     * reached by a full-page navigation to a minted sign-on URL, so nothing in
     * the app routes or links to jobs.careerpathdecide.org directly.
     */
    path: 'job-board',
    title: 'Job board',
    loadChildren: () =>
      import(`./features/job-board/job-board.module`).then(
        (m) => m.JobBoardModule
      ),
    canActivate: [siteBlockGuard, frontDoorGuard]
  },
  {
    path: 'goals-report',
    title: 'Your Goals Report',
    loadChildren: () =>
      import(`./features/goals-report/goals-report.module`).then(
        (m) => m.GoalsReportModule
      ),
    canActivate: [siteBlockGuard, frontDoorGuard]
  },
  {
    path: 'analytics',
    title: 'Analytics',
    loadChildren: () =>
      import('./features/analytics/analytics.module').then(
        (m) => m.AnalyticsModule
      ),
    canActivate: [
      siteBlockGuard,
      sessionSettledGuard,
      authGuard,
      councelorRoleGuard
    ]
  },
  {
    path: 'account',
    title: 'Account',
    loadChildren: () =>
      import('./features/account/account.module').then((m) => m.AccountModule),
    canActivate: [siteBlockGuard]
  },
  {
    // THIS ROUTE IS NEEDED AS WE NEED TO SUPPORT RETURN SEESSION FUNCTIONALITY FOR THE OLD LINKS
    path: 'returnsession',
    title: 'Return to your session',
    loadChildren: () =>
      import('./features/return-session/return-session.module').then(
        (m) => m.ReturnSessionModule
      ),
    canActivate: [siteBlockGuard]
  },
  {
    // INFRA-4: deliberately NOT behind siteBlockGuard. This is where an admin
    // turns the site block back off, so blocking it means the only way out of
    // a site block is manual SQL. Still gated by authGuard + adminRoleGuard,
    // so this is an exemption from the maintenance block, not from auth.
    // Paired with the matching exemption in maintance.effects.ts; removing
    // either one alone puts the lockout back.
    path: 'manager',
    title: 'Administration',
    loadChildren: () =>
      import('./features/manager/manager.module').then((m) => m.ManagerModule),
    canActivate: [sessionSettledGuard, authGuard, adminRoleGuard]
  },
  {
    path: 'about',
    title: 'About',
    loadChildren: () =>
      import('./features/about/about.module').then((m) => m.AboutModule),
    canActivate: [siteBlockGuard, frontDoorGuard]
  },
  {
    path: 'accept-terms',
    title: 'Notice and consent',
    loadChildren: () =>
      import('./features/privacy-statement/privacy-statement.module').then(
        (m) => m.PrivacyStatementModule
      ),
    canActivate: [siteBlockGuard]
  },
  {
    path: 'profile-work-type',
    title: 'Tell us about yourself',
    loadChildren: () =>
      import('./features/profile-work-type/profile-work-type.module').then(
        (m) => m.ProfileWorkTypeModule
      ),
    canActivate: [siteBlockGuard]
  },
  {
    path: 'error/:code',
    /*
     * The page's own heading, for its code (2.4.2). Every code was titled
     * "Page not found", so a 403 (Session Expired) or a 500 said the wrong
     * thing in the tab and to a screen reader.
     */
    title: (route) =>
      ERROR_CODES.find((item) => item.code === route.paramMap.get('code'))
        ?.title?.trim() || 'Page not found',
    loadChildren: () =>
      import('./features/error/error.module').then((m) => m.ErrorModule)
  },
  {
    path: 'passcode',
    title: 'Enter your passcode',
    component: PasscodeComponent
  },
  {
    path: '**',
    redirectTo: 'error/404'
  }
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      anchorScrolling: 'enabled',
      onSameUrlNavigation: 'reload',
      scrollPositionRestoration: 'enabled',
      preloadingStrategy: CustomPreloadingStrategy
    })
  ],
  exports: [RouterModule],
  providers: [
    { provide: TitleStrategy, useClass: CpdTitleStrategy },
    CustomPreloadingStrategy
  ]
})
export class AppRoutingModule {
  readonly careerPlanningUrls = [
    '/career-planning/quick-match',
    '/career-planning/build-your-target'
  ];

  readonly takeSurveyUrls = TAKE_SURVEY_URLS;

  constructor(router: Router, viewportScroller: ViewportScroller) {
    zip(
      router.events.pipe(
        filter((e: Event): e is Scroll => e instanceof Scroll)
      ),
      router.events.pipe(
        filter((e2: Event): e2 is NavigationEnd => e2 instanceof NavigationEnd)
      )
    ).subscribe(([e, e2]) => {
      if (e.position) {
        // backward navigation
        viewportScroller.scrollToPosition(e.position);
      } else {
        // forward navigation
        const url = e2.url;
        if (this.careerPlanningUrls.includes(url)) {
          viewportScroller.scrollToAnchor('career-planning-tabs');
        } else if (this.takeSurveyUrls.includes(url)) {
          timer(500)
            .pipe(take(1))
            .subscribe(() => {
              document.getElementById(BUILD_TARGET).scrollIntoView({
                behavior: 'smooth'
              });
            });
        } else {
          viewportScroller.scrollToPosition([0, 0]);
          // App shell uses an inner scroll container (.site-container-wrapper);
          // window/body never scroll, so scrollToPosition is a no-op for the
          // visible viewport. Reset the wrapper directly.
          const wrapper = document.querySelector<HTMLElement>(
            '.site-container-wrapper'
          );
          if (wrapper) {
            wrapper.scrollTop = 0;
          }
        }
      }
    });
  }
}
